import { useEffect, useState } from "react";
import { MEDIA } from "../theme/breakpoints";

/**
 * Is the viewport phone-sized right now?
 *
 * Most of this app styles with inline objects rather than CSS classes, so a
 * media query in a stylesheet can't reach it — `isMobile ? a : b` is the only
 * lever that works everywhere without a rewrite. (The sidebar is a clear case:
 * its width is a Framer Motion prop, so no stylesheet could override it.)
 *
 * Plain matchMedia rather than MUI's useMediaQuery, since most pages here don't
 * import MUI and shouldn't have to start.
 */
export function useMediaQueryMatch(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);

    // Keep in step if the query itself changes, not just the viewport.
    setMatches(mql.matches);

    // addListener is the Safari < 14 spelling; still worth keeping.
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

/** Phone-sized — the sidebar is a drawer and layouts go single-column. */
export default function useIsMobile() {
  return useMediaQueryMatch(MEDIA.mobile);
}

/** Tablet or narrower — grids may drop columns but the shell is unchanged. */
export function useIsTablet() {
  return useMediaQueryMatch(MEDIA.tablet);
}
